import React from 'react';
import { AlmanacCalendarProviderType } from '@bb/domain/dist/types';
import errorReporter from '@app/errorReporter';
import { AlmanacController } from 'api';
import { useSearchParams } from 'hooks';
import { UpdateAlmanacAgentUserSubscriptionBody } from 'api/AlmanacController';
import {
  AgentCalendarMenu,
  ListingCalendarMenu,
  ShowingCalendarMenuOption,
} from './types';

type AgentUserCalendarContextValue = {
  isAuthenticated: boolean;
  setNotAuthenticated: () => void;
  existingCalendarProvider: AlmanacCalendarProviderType | 'none';
  setExistingCalendarProvider: React.Dispatch<
    React.SetStateAction<AlmanacCalendarProviderType | 'none'>
  >;
  calendarProvider: AlmanacCalendarProviderType | 'none';
  setCalendarProvider: React.Dispatch<
    React.SetStateAction<AlmanacCalendarProviderType | 'none'>
  >;
  agentUserCalendarSubscriptions: UpdateAlmanacAgentUserSubscriptionBody;
  setAgentUserCalendarSubscriptions: React.Dispatch<
    React.SetStateAction<UpdateAlmanacAgentUserSubscriptionBody>
  >;
  redirectToCalendarSyncOauth: (args: {
    service: AlmanacCalendarProviderType;
    agent: ShowingCalendarMenuOption;
    appointmentIds?: string[];
    listingIds?: string[]; // listings to sync
    listingId?: string; // listing to redirect to, if on individual listing page
  }) => void;
};

const AgentUserCalendarContext =
  React.createContext<AgentUserCalendarContextValue>(undefined);

export const AgentUserCalendarContextProvider: React.FC = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [calendarProvider, setCalendarProvider] = React.useState<
    AlmanacCalendarProviderType | 'none'
  >();
  const [existingCalendarProvider, setExistingCalendarProvider] =
    React.useState<AlmanacCalendarProviderType>();
  const [agentUserCalendarSubscriptions, setAgentUserCalendarSubscriptions] =
    React.useState<UpdateAlmanacAgentUserSubscriptionBody>();

  const searchParams = useSearchParams();

  React.useEffect(() => {
    (async () => {
      try {
        const [
          {
            isAuthenticated: _isAuthenticated,
            calendarProvider: _calendarProvider,
            showBuyerShowingsAsBusy,
            showListingShowingsAsBusy,
          },
          { coop, agent },
        ] = await Promise.all([
          AlmanacController.isAuthenticatedForAgent(),
          AlmanacController.findAlmanacAgentUserSubscription(),
        ]);

        setIsAuthenticated(_isAuthenticated);
        setCalendarProvider(_calendarProvider || 'none');
        setExistingCalendarProvider(_calendarProvider);
        setAgentUserCalendarSubscriptions({
          buyerShowingCalendarSubscription: !!(
            coop || searchParams.buyerShowingCalendarSubscription
          ),
          listingShowingCalendarSubscription: !!(
            agent || searchParams.listingShowingCalendarSubscription
          ),
          showBuyerShowingsAsBusy,
          showListingShowingsAsBusy,
        });
      } catch (err) {
        errorReporter.error(err);
      }
    })();
  }, []);

  const redirectToCalendarSyncOauth: AgentUserCalendarContextValue['redirectToCalendarSyncOauth'] =
    ({ service, agent, appointmentIds, listingIds, listingId }) => {
      let calendarType;

      switch (agent) {
        case ListingCalendarMenu.InPerson:
          calendarType = 'individual';
          break;
        case AgentCalendarMenu.Listing:
          calendarType = 'listing';
          break;
        case AgentCalendarMenu.Buyer:
          calendarType = 'buyer';
          break;
        default:
          throw new Error(`Unsupported agent: ${agent}`);
      }

      const params = new URLSearchParams({
        calendarSyncRedirect: 'true',
      });

      if (appointmentIds?.length) {
        appointmentIds.forEach(apptId =>
          params.append('syncAppointmentIds', apptId),
        );
      } else if (listingIds?.length) {
        listingIds?.forEach(_listingId =>
          params.append('syncListingIds', _listingId),
        );
      }

      let redirectUrl = `${window.location.origin}/#/`;

      if (calendarType === 'individual') {
        redirectUrl = redirectUrl.concat(`listing/${listingId}/showings`);
      } else {
        redirectUrl = redirectUrl.concat(
          `my_business/showings/${calendarType}`,
        );
      }
      redirectUrl = redirectUrl.concat(`?${params.toString()}`);

      const oauthUrl = `/almanac/connect?service=${service}&redirectUrl=${encodeURIComponent(
        redirectUrl,
      )}`;

      window.location.href = oauthUrl;
    };

  return (
    <AgentUserCalendarContext.Provider
      value={{
        isAuthenticated,
        setNotAuthenticated: () => {
          setIsAuthenticated(false);
          setExistingCalendarProvider(undefined);
        },
        calendarProvider,
        setCalendarProvider,
        existingCalendarProvider,
        setExistingCalendarProvider,
        agentUserCalendarSubscriptions,
        setAgentUserCalendarSubscriptions,
        redirectToCalendarSyncOauth,
      }}
    >
      {children}
    </AgentUserCalendarContext.Provider>
  );
};

export default AgentUserCalendarContext;
